/* eslint-disable */
/*
 * QRCode.js - self-contained, dependency-free QR Code generator (ES module).
 *
 * Port/adaptation of the "QR Code generator library" by Project Nayuki
 * (https://www.nayuki.io/page/qr-code-generator-library), MIT License.
 * Copyright (c) Project Nayuki. Reproduced faithfully (exact ECC tables,
 * Reed-Solomon over GF(256) with 0x11D, BCH format info XOR 0x5412, all 8
 * data masks and the 4 standard penalty rules).
 *
 * This adaptation:
 *  - Byte mode only, UTF-8 encoding of the input string.
 *  - Auto-selects the smallest fitting version (supports versions 1..40).
 *  - No npm deps, no Node built-ins, no canvas. Written in plain ES2015
 *    (var/function, no optional chaining / BigInt) so it transpiles safely
 *    for old TV browsers (Chrome 38+).
 *
 * Public API (default export):
 *   QRCode.generate(text, { ecc })            -> { size, modules }
 *   QRCode.toSVG(text, { ecc, moduleSize, margin, dark, light }) -> String
 *   QRCode.toDataURL(text, opts)              -> "data:image/svg+xml;base64,..."
 *
 * License: MIT.
 */

'use strict';

/* ---------------------------------------------------------------------- *
 * Error-correction level metadata
 * ordinal / formatBits per the QR spec (nayuki ordering)
 * ---------------------------------------------------------------------- */
var ECC = {
  L: { ordinal: 0, formatBits: 1 },
  M: { ordinal: 1, formatBits: 0 },
  Q: { ordinal: 2, formatBits: 3 },
  H: { ordinal: 3, formatBits: 2 }
};

var MIN_VERSION = 1;
var MAX_VERSION = 40;

var PENALTY_N1 = 3;
var PENALTY_N2 = 3;
var PENALTY_N3 = 40;
var PENALTY_N4 = 10;

/* ECC codewords per block, indexed [ordinal][version] (version 0 unused). */
var ECC_CODEWORDS_PER_BLOCK = [
  // 0   1   2   3   4   5   6   7   8   9  10  11  12  13  14  15  16  17  18  19  20  21  22  23  24  25  26  27  28  29  30  31  32  33  34  35  36  37  38  39  40
  [-1,  7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // L
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28], // M
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // Q
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30]  // H
];

/* Number of error-correction blocks, indexed [ordinal][version]. */
var NUM_ERROR_CORRECTION_BLOCKS = [
  // 0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38 39 40
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9,10,12,12,12,13,14,15,16,17,18,19,19,20,21,22,24,25], // L
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9,10,10,11,13,14,16,17,17,18,20,21,23,25,26,28,29,31,33,35,37,38,40,43,45,47,49], // M
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8,10,12,16,12,17,16,18,21,20,23,23,25,27,29,34,34,35,38,40,43,45,48,51,53,56,59,62,65,68], // Q
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8,11,11,16,16,18,16,19,21,25,25,25,34,30,32,35,37,40,42,45,48,51,54,57,60,63,66,70,74,77,81]  // H
];

/* ---------------------------------------------------------------------- *
 * Small helpers
 * ---------------------------------------------------------------------- */
function getBit(x, i) {
  return ((x >>> i) & 1) !== 0;
}

/* Convert a JS string to an array of UTF-8 byte values (0..255). */
function toUtf8Bytes(str) {
  var bytes = [];
  var i, c, hi, lo, cp;
  for (i = 0; i < str.length; i++) {
    c = str.charCodeAt(i);
    if (c < 0x80) {
      bytes.push(c);
    } else if (c < 0x800) {
      bytes.push(0xC0 | (c >> 6));
      bytes.push(0x80 | (c & 0x3F));
    } else if (c >= 0xD800 && c <= 0xDBFF && i + 1 < str.length) {
      // High surrogate: combine with following low surrogate.
      hi = c;
      lo = str.charCodeAt(i + 1);
      if (lo >= 0xDC00 && lo <= 0xDFFF) {
        i++;
        cp = 0x10000 + ((hi - 0xD800) << 10) + (lo - 0xDC00);
        bytes.push(0xF0 | (cp >> 18));
        bytes.push(0x80 | ((cp >> 12) & 0x3F));
        bytes.push(0x80 | ((cp >> 6) & 0x3F));
        bytes.push(0x80 | (cp & 0x3F));
      } else {
        // Unpaired surrogate; encode as 3-byte (lenient).
        bytes.push(0xE0 | (c >> 12));
        bytes.push(0x80 | ((c >> 6) & 0x3F));
        bytes.push(0x80 | (c & 0x3F));
      }
    } else {
      bytes.push(0xE0 | (c >> 12));
      bytes.push(0x80 | ((c >> 6) & 0x3F));
      bytes.push(0x80 | (c & 0x3F));
    }
  }
  return bytes;
}

/* ---------------------------------------------------------------------- *
 * Reed-Solomon over GF(256) with primitive polynomial 0x11D
 * ---------------------------------------------------------------------- */
function reedSolomonMultiply(x, y) {
  var z = 0;
  var i;
  for (i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11D);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xFF;
}

/* Returns the generator polynomial (divisor) coefficients for `degree`. */
function reedSolomonComputeDivisor(degree) {
  var result = [];
  var i, j, root;
  for (i = 0; i < degree - 1; i++) {
    result.push(0);
  }
  result.push(1); // Monomial x^0

  root = 1;
  for (i = 0; i < degree; i++) {
    for (j = 0; j < result.length; j++) {
      result[j] = reedSolomonMultiply(result[j], root);
      if (j + 1 < result.length) {
        result[j] ^= result[j + 1];
      }
    }
    root = reedSolomonMultiply(root, 0x02);
  }
  return result;
}

/* Returns the RS remainder (error-correction codewords). */
function reedSolomonComputeRemainder(data, divisor) {
  var result = [];
  var i, k, factor;
  for (i = 0; i < divisor.length; i++) {
    result.push(0);
  }
  for (k = 0; k < data.length; k++) {
    factor = data[k] ^ result.shift();
    result.push(0);
    for (i = 0; i < divisor.length; i++) {
      result[i] ^= reedSolomonMultiply(divisor[i], factor);
    }
  }
  return result;
}

/* ---------------------------------------------------------------------- *
 * Capacity / geometry helpers
 * ---------------------------------------------------------------------- */
function getNumRawDataModules(ver) {
  var result = (16 * ver + 128) * ver + 64;
  var numAlign;
  if (ver >= 2) {
    numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) {
      result -= 36;
    }
  }
  return result;
}

function getNumDataCodewords(ver, eccOrdinal) {
  return Math.floor(getNumRawDataModules(ver) / 8) -
    ECC_CODEWORDS_PER_BLOCK[eccOrdinal][ver] *
    NUM_ERROR_CORRECTION_BLOCKS[eccOrdinal][ver];
}

/* Character-count indicator length for byte mode at a given version. */
function byteModeCountBits(ver) {
  if (ver <= 9) return 8;
  return 16; // versions 10..40
}

/* ---------------------------------------------------------------------- *
 * Data segment construction (byte mode) + ECC interleaving
 * ---------------------------------------------------------------------- */

/* Builds the full sequence of data + ECC codewords, interleaved, ready to
 * be laid onto the matrix. `dataCodewords` must already be padded to the
 * exact capacity for the version/ECC. */
function addEccAndInterleave(dataCodewords, ver, eccOrdinal) {
  var numBlocks = NUM_ERROR_CORRECTION_BLOCKS[eccOrdinal][ver];
  var blockEccLen = ECC_CODEWORDS_PER_BLOCK[eccOrdinal][ver];
  var rawCodewords = Math.floor(getNumRawDataModules(ver) / 8);
  var numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  var shortBlockLen = Math.floor(rawCodewords / numBlocks);

  var blocks = [];
  var rsDiv = reedSolomonComputeDivisor(blockEccLen);
  var i, j, k, dataLen, dat, ecc;

  k = 0;
  for (i = 0; i < numBlocks; i++) {
    dataLen = shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1);
    dat = dataCodewords.slice(k, k + dataLen);
    k += dataLen;
    ecc = reedSolomonComputeRemainder(dat, rsDiv);
    if (i < numShortBlocks) {
      dat.push(0); // padding slot to align interleaving
    }
    blocks.push(dat.concat(ecc));
  }

  var result = [];
  var maxLen = blocks[0].length;
  for (i = 0; i < maxLen; i++) {
    for (j = 0; j < blocks.length; j++) {
      // Skip the padding cell present only in short data blocks.
      if (i !== (shortBlockLen - blockEccLen) || j >= numShortBlocks) {
        result.push(blocks[j][i]);
      }
    }
  }
  return result;
}

/* ---------------------------------------------------------------------- *
 * QR matrix construction
 * ---------------------------------------------------------------------- */
function makeGrid(size, fill) {
  var grid = [];
  var y, x, row;
  for (y = 0; y < size; y++) {
    row = [];
    for (x = 0; x < size; x++) {
      row.push(fill);
    }
    grid.push(row);
  }
  return grid;
}

function getAlignmentPatternPositions(ver, size) {
  if (ver === 1) return [];
  var numAlign = Math.floor(ver / 7) + 2;
  var step;
  if (ver === 32) {
    step = 26;
  } else {
    step = Math.ceil((size - 13) / (numAlign * 2 - 2)) * 2;
  }
  var result = [6];
  var pos;
  for (pos = size - 7; result.length < numAlign; pos -= step) {
    result.splice(1, 0, pos);
  }
  return result;
}

/* Core builder. Returns { size, modules } (modules[row][col] boolean). */
function buildMatrix(dataCodewords, ver, eccInfo) {
  var size = ver * 4 + 17;
  var modules = makeGrid(size, false);
  var isFunction = makeGrid(size, false);

  function setFunctionModule(x, y, isDark) {
    modules[y][x] = isDark;
    isFunction[y][x] = true;
  }

  function drawFinderPattern(x, y) {
    var dy, dx, dist, xx, yy;
    for (dy = -4; dy <= 4; dy++) {
      for (dx = -4; dx <= 4; dx++) {
        dist = Math.max(Math.abs(dx), Math.abs(dy));
        xx = x + dx;
        yy = y + dy;
        if (xx >= 0 && xx < size && yy >= 0 && yy < size) {
          setFunctionModule(xx, yy, dist !== 2 && dist !== 4);
        }
      }
    }
  }

  function drawAlignmentPattern(x, y) {
    var dy, dx;
    for (dy = -2; dy <= 2; dy++) {
      for (dx = -2; dx <= 2; dx++) {
        setFunctionModule(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  }

  function drawFormatBits(mask) {
    var data = (eccInfo.formatBits << 3) | mask; // 5 bits
    var rem = data;
    var i, bits;
    for (i = 0; i < 10; i++) {
      rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    }
    bits = ((data << 10) | rem) ^ 0x5412; // 15 bits, BCH + mask
    // First copy.
    for (i = 0; i <= 5; i++) setFunctionModule(8, i, getBit(bits, i));
    setFunctionModule(8, 7, getBit(bits, 6));
    setFunctionModule(8, 8, getBit(bits, 7));
    setFunctionModule(7, 8, getBit(bits, 8));
    for (i = 9; i < 15; i++) setFunctionModule(14 - i, 8, getBit(bits, i));
    // Second copy.
    for (i = 0; i < 8; i++) setFunctionModule(size - 1 - i, 8, getBit(bits, i));
    for (i = 8; i < 15; i++) setFunctionModule(8, size - 15 + i, getBit(bits, i));
    setFunctionModule(8, size - 8, true); // always dark
  }

  function drawVersion() {
    if (ver < 7) return;
    var rem = ver;
    var i, bits, a, b, color;
    for (i = 0; i < 12; i++) {
      rem = (rem << 1) ^ ((rem >>> 11) * 0x1F25);
    }
    bits = (ver << 12) | rem; // 18 bits
    for (i = 0; i < 18; i++) {
      color = getBit(bits, i);
      a = size - 11 + (i % 3);
      b = Math.floor(i / 3);
      setFunctionModule(a, b, color);
      setFunctionModule(b, a, color);
    }
  }

  function drawFunctionPatterns() {
    var i, j;
    // Timing patterns.
    for (i = 0; i < size; i++) {
      setFunctionModule(6, i, i % 2 === 0);
      setFunctionModule(i, 6, i % 2 === 0);
    }
    // Finder patterns (with separators handled by the 4-wide loop).
    drawFinderPattern(3, 3);
    drawFinderPattern(size - 4, 3);
    drawFinderPattern(3, size - 4);
    // Alignment patterns.
    var alignPos = getAlignmentPatternPositions(ver, size);
    var n = alignPos.length;
    for (i = 0; i < n; i++) {
      for (j = 0; j < n; j++) {
        if (!((i === 0 && j === 0) || (i === 0 && j === n - 1) || (i === n - 1 && j === 0))) {
          drawAlignmentPattern(alignPos[i], alignPos[j]);
        }
      }
    }
    // Reserve format/version areas (real bits written later).
    drawFormatBits(0);
    drawVersion();
  }

  function drawCodewords(data) {
    var i = 0; // bit index into the entire data stream
    var right, vert, j, x, y, upward;
    for (right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5; // skip vertical timing column
      for (vert = 0; vert < size; vert++) {
        for (j = 0; j < 2; j++) {
          x = right - j;
          upward = ((right + 1) & 2) === 0;
          y = upward ? size - 1 - vert : vert;
          if (!isFunction[y][x] && i < data.length * 8) {
            modules[y][x] = getBit(data[i >>> 3], 7 - (i & 7));
            i++;
          }
        }
      }
    }
  }

  function applyMask(mask) {
    var y, x, invert, val;
    for (y = 0; y < size; y++) {
      for (x = 0; x < size; x++) {
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break;
          case 1: invert = y % 2 === 0; break;
          case 2: invert = x % 3 === 0; break;
          case 3: invert = (x + y) % 3 === 0; break;
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: invert = false;
        }
        if (!isFunction[y][x] && invert) {
          val = modules[y][x];
          modules[y][x] = !val;
        }
      }
    }
  }

  /* --- Penalty scoring (4 standard rules) --- */
  function finderPenaltyAddHistory(currentRunLength, runHistory) {
    if (runHistory[0] === 0) {
      currentRunLength += size; // add light border to initial run
    }
    runHistory.pop();
    runHistory.unshift(currentRunLength);
  }

  function finderPenaltyCountPatterns(runHistory) {
    var n = runHistory[1];
    var core = n > 0 && runHistory[2] === n && runHistory[3] === n * 3 &&
      runHistory[4] === n && runHistory[5] === n;
    return (core && runHistory[0] >= n * 4 && runHistory[6] >= n ? 1 : 0) +
      (core && runHistory[6] >= n * 4 && runHistory[0] >= n ? 1 : 0);
  }

  function finderPenaltyTerminateAndCount(currentRunColor, currentRunLength, runHistory) {
    if (currentRunColor) {
      finderPenaltyAddHistory(currentRunLength, runHistory);
      currentRunLength = 0;
    }
    currentRunLength += size; // add light border to final run
    finderPenaltyAddHistory(currentRunLength, runHistory);
    return finderPenaltyCountPatterns(runHistory);
  }

  function getPenaltyScore() {
    var result = 0;
    var x, y, runColor, runLen, runHistory, color, dark, total, k;

    // Rule 1 & 3 (rows).
    for (y = 0; y < size; y++) {
      runColor = false;
      runLen = 0;
      runHistory = [0, 0, 0, 0, 0, 0, 0];
      for (x = 0; x < size; x++) {
        if (modules[y][x] === runColor) {
          runLen++;
          if (runLen === 5) result += PENALTY_N1;
          else if (runLen > 5) result++;
        } else {
          finderPenaltyAddHistory(runLen, runHistory);
          if (!runColor) result += finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
          runColor = modules[y][x];
          runLen = 1;
        }
      }
      result += finderPenaltyTerminateAndCount(runColor, runLen, runHistory) * PENALTY_N3;
    }
    // Rule 1 & 3 (columns).
    for (x = 0; x < size; x++) {
      runColor = false;
      runLen = 0;
      runHistory = [0, 0, 0, 0, 0, 0, 0];
      for (y = 0; y < size; y++) {
        if (modules[y][x] === runColor) {
          runLen++;
          if (runLen === 5) result += PENALTY_N1;
          else if (runLen > 5) result++;
        } else {
          finderPenaltyAddHistory(runLen, runHistory);
          if (!runColor) result += finderPenaltyCountPatterns(runHistory) * PENALTY_N3;
          runColor = modules[y][x];
          runLen = 1;
        }
      }
      result += finderPenaltyTerminateAndCount(runColor, runLen, runHistory) * PENALTY_N3;
    }
    // Rule 2: 2x2 blocks of same color.
    for (y = 0; y < size - 1; y++) {
      for (x = 0; x < size - 1; x++) {
        color = modules[y][x];
        if (color === modules[y][x + 1] && color === modules[y + 1][x] && color === modules[y + 1][x + 1]) {
          result += PENALTY_N2;
        }
      }
    }
    // Rule 4: dark/light balance.
    dark = 0;
    for (y = 0; y < size; y++) {
      for (x = 0; x < size; x++) {
        if (modules[y][x]) dark++;
      }
    }
    total = size * size;
    k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    result += k * PENALTY_N4;
    return result;
  }

  // --- Assemble ---
  drawFunctionPatterns();
  var allCodewords = addEccAndInterleave(dataCodewords, ver, eccInfo.ordinal);
  drawCodewords(allCodewords);

  // Choose the mask with the lowest penalty.
  var minPenalty = Infinity;
  var bestMask = 0;
  var mask, penalty;
  for (mask = 0; mask < 8; mask++) {
    applyMask(mask);
    drawFormatBits(mask);
    penalty = getPenaltyScore();
    if (penalty < minPenalty) {
      minPenalty = penalty;
      bestMask = mask;
    }
    applyMask(mask); // undo (XOR is its own inverse)
  }
  applyMask(bestMask);
  drawFormatBits(bestMask);

  return { size: size, modules: modules, version: ver, mask: bestMask };
}

/* ---------------------------------------------------------------------- *
 * Encoding pipeline
 * ---------------------------------------------------------------------- */
function encode(text, eccKey) {
  var key = (eccKey || 'M').toUpperCase();
  var eccInfo = ECC[key];
  if (!eccInfo) {
    throw new Error('QRCode: invalid ECC level "' + eccKey + '" (use L, M, Q or H)');
  }

  var bytes = toUtf8Bytes(text);
  var numBytes = bytes.length;

  // Pick smallest version that fits.
  var ver = -1;
  var v, capacityBits, neededBits;
  for (v = MIN_VERSION; v <= MAX_VERSION; v++) {
    capacityBits = getNumDataCodewords(v, eccInfo.ordinal) * 8;
    neededBits = 4 + byteModeCountBits(v) + 8 * numBytes;
    if (neededBits <= capacityBits) {
      ver = v;
      break;
    }
  }
  if (ver === -1) {
    throw new Error('QRCode: data too long to fit in any supported version (max 40)');
  }

  var dataCapacityCodewords = getNumDataCodewords(ver, eccInfo.ordinal);

  // Build the bit stream.
  var bits = [];
  function appendBits(val, len) {
    var i;
    for (i = len - 1; i >= 0; i--) {
      bits.push((val >>> i) & 1);
    }
  }

  appendBits(0x4, 4); // byte-mode indicator
  appendBits(numBytes, byteModeCountBits(ver));
  var i;
  for (i = 0; i < numBytes; i++) {
    appendBits(bytes[i], 8);
  }

  var capacityBitsFinal = dataCapacityCodewords * 8;
  // Terminator (up to 4 zero bits).
  var terminator = Math.min(4, capacityBitsFinal - bits.length);
  appendBits(0, terminator);
  // Pad to a byte boundary.
  appendBits(0, (8 - (bits.length % 8)) % 8);

  // Convert bits to codewords.
  var dataCodewords = [];
  for (i = 0; i < bits.length; i += 8) {
    var b = 0, j;
    for (j = 0; j < 8; j++) {
      b = (b << 1) | bits[i + j];
    }
    dataCodewords.push(b);
  }
  // Pad bytes 0xEC / 0x11 alternating.
  var pad = 0xEC;
  while (dataCodewords.length < dataCapacityCodewords) {
    dataCodewords.push(pad);
    pad = (pad === 0xEC) ? 0x11 : 0xEC;
  }

  return buildMatrix(dataCodewords, ver, eccInfo);
}

/* ---------------------------------------------------------------------- *
 * Base64 (UTF-8 aware, works in Node and browsers without Buffer/btoa)
 * ---------------------------------------------------------------------- */
var B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function base64FromString(str) {
  var bytes = toUtf8Bytes(str);
  var out = '';
  var i, b0, b1, b2;
  for (i = 0; i < bytes.length; i += 3) {
    b0 = bytes[i];
    b1 = (i + 1 < bytes.length) ? bytes[i + 1] : 0;
    b2 = (i + 2 < bytes.length) ? bytes[i + 2] : 0;
    out += B64_CHARS.charAt(b0 >> 2);
    out += B64_CHARS.charAt(((b0 & 0x03) << 4) | (b1 >> 4));
    out += (i + 1 < bytes.length) ? B64_CHARS.charAt(((b1 & 0x0F) << 2) | (b2 >> 6)) : '=';
    out += (i + 2 < bytes.length) ? B64_CHARS.charAt(b2 & 0x3F) : '=';
  }
  return out;
}

/* ---------------------------------------------------------------------- *
 * Public API
 * ---------------------------------------------------------------------- */
var QRCode = {
  /* Returns { size, modules } where modules[row][col] === true means dark. */
  generate: function (text, opts) {
    opts = opts || {};
    var res = encode(text, opts.ecc);
    return { size: res.size, modules: res.modules, version: res.version, mask: res.mask };
  },

  /* Returns a self-contained SVG string. */
  toSVG: function (text, opts) {
    opts = opts || {};
    var moduleSize = (opts.moduleSize == null) ? 8 : opts.moduleSize;
    var margin = (opts.margin == null) ? 4 : opts.margin;
    var dark = (opts.dark == null) ? '#000' : opts.dark;
    var light = (opts.light == null) ? '#fff' : opts.light;

    var res = encode(text, opts.ecc);
    var size = res.size;
    var modules = res.modules;
    var dim = (size + margin * 2) * moduleSize;

    var parts = [];
    var r, c, x, y;
    for (r = 0; r < size; r++) {
      for (c = 0; c < size; c++) {
        if (modules[r][c]) {
          x = (c + margin) * moduleSize;
          y = (r + margin) * moduleSize;
          parts.push('M' + x + ',' + y + 'h' + moduleSize + 'v' + moduleSize + 'h-' + moduleSize + 'z');
        }
      }
    }

    return '<svg xmlns="http://www.w3.org/2000/svg" width="' + dim + '" height="' + dim +
      '" viewBox="0 0 ' + dim + ' ' + dim + '" shape-rendering="crispEdges">' +
      '<rect width="' + dim + '" height="' + dim + '" fill="' + light + '"/>' +
      '<path d="' + parts.join('') + '" fill="' + dark + '"/>' +
      '</svg>';
  },

  /* Returns a base64-encoded SVG data URL. */
  toDataURL: function (text, opts) {
    var svg = this.toSVG(text, opts);
    return 'data:image/svg+xml;base64,' + base64FromString(svg);
  }
};

export default QRCode;



// WEBPACK FOOTER //
// ./src/helpers/QRCode.js